import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api";

/* ═══════════════════════════════════════════════════════════
   고객 배송 현황 페이지 — 이메일 링크로 접근 (로그인 불필요)
   "76% 소비자: 배송 지연보다 안내 없는 나쁜 소통이 더 최악"
   ═══════════════════════════════════════════════════════════ */

const STATUS_STEPS = [
  { key: "preparing", label: "준비 중", index: "01" },
  { key: "departed", label: "출발", index: "02" },
  { key: "delayed", label: "경로 확인", index: "03" },
  { key: "arrived", label: "도착", index: "04" },
  { key: "completed", label: "완료", index: "05" },
];

const TRAFFIC_STYLE = {
  "원활": { color: "#10b981", bg: "#ecfdf5", label: "원활" },
  "보통": { color: "#eab308", bg: "#fefce8", label: "보통" },
  "혼잡": { color: "#f97316", bg: "#fff7ed", label: "혼잡" },
  "정체": { color: "#ef4444", bg: "#fef2f2", label: "정체" },
};

export default function DeliveryStatusPage() {
  const { routeCode } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!routeCode) return;
    setLoading(true);
    api.get(`/api/delivery/${routeCode}`)
      .then(r => { if (r.data.ok) setRoute(r.data.route); else setError("배송 정보를 찾을 수 없습니다"); })
      .catch(() => setError("배송 정보를 불러올 수 없습니다"))
      .finally(() => setLoading(false));
  }, [routeCode]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"/>
          <p className="text-sm text-gray-400 mt-4">배송 현황을 확인하고 있습니다...</p>
        </div>
      </div>
    );
  }

  if (error || !route) {
    return (
      <div className="dept-access-page">
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">조회</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">배송 현황</span>
            <h1>배송 정보를 찾을 수 없습니다</h1>
            <p>{error || "유효하지 않은 배송 코드입니다"}</p>
          </div>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_STEPS.findIndex(s => s.key === route.status);
  const isCancelled = route.status === "cancelled";
  const ai = route.ai_summary;
  const trafficStyle = ai?.trafficLevel ? TRAFFIC_STYLE[ai.trafficLevel] : null;
  const purposeLabel = { delivery:"납품 배송", install:"설치 방문", as_dispatch:"AS 출장", rental:"렌탈 설치 방문" }[route.purpose] || "방문";

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      {/* Header Card */}
      <div className="bg-gradient-to-br from-[#1e293b] to-[#334155] rounded-2xl p-6 text-white mb-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-xs font-bold">IL</div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">일광오피스</h1>
            <p className="text-[11px] text-white/50">실시간 배송 현황 · Smart Logistics</p>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-white/50">코드</span><span className="font-mono font-semibold">{route.route_code}</span></div>
          <div className="flex justify-between"><span className="text-white/50">목적</span><span>{purposeLabel}</span></div>
          <div className="flex justify-between"><span className="text-white/50">담당</span><span>{route.engineer_name}</span></div>
          {route.estimated_arrival && (
            <div className="flex justify-between"><span className="text-white/50">예상 도착</span><span>{route.estimated_arrival?.slice(0,16)}</span></div>
          )}
        </div>
      </div>

      {/* Traffic Status */}
      {ai && trafficStyle && (
        <div className="rounded-2xl p-5 mb-6 border" style={{ background: trafficStyle.bg, borderColor: trafficStyle.color + "30" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: trafficStyle.color }}/>
            <span className="text-sm font-bold" style={{ color: trafficStyle.color }}>교통 상태: {trafficStyle.label}</span>
          </div>
          {ai.summary && <p className="text-sm text-gray-600 leading-relaxed">{ai.summary}</p>}
          {ai.estimatedDelay && (
            <p className="text-xs text-gray-500 mt-2">예상 지연: {ai.estimatedDelay}</p>
          )}
          <p className="text-[10px] text-gray-400 mt-3 pt-2 border-t" style={{ borderColor: trafficStyle.color + "20" }}>
            국토교통부 ITS 공공데이터 기반 실시간 교통 분석 시스템 · Gemini AI
          </p>
        </div>
      )}

      {/* Status Progress */}
      {!isCancelled ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 shadow-sm">
          <h3 className="text-sm font-bold text-gray-800 mb-5">배송 진행 상태</h3>
          <div className="space-y-0">
            {STATUS_STEPS.map((step, i) => {
              const isActive = i <= currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <div key={step.key} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                      isCurrent ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30 scale-110"
                        : isActive ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-400"
                    }`}>{step.index}</div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div className={`w-0.5 h-8 ${isActive ? "bg-blue-300" : "bg-gray-200"}`}/>
                    )}
                  </div>
                  <div className="pb-6">
                    <p className={`text-sm font-medium ${isCurrent ? "text-blue-600 font-bold" : isActive ? "text-gray-700" : "text-gray-400"}`}>
                      {step.label}
                      {isCurrent && <span className="ml-2 text-xs text-blue-400 animate-pulse">● 현재</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-6 mb-6 text-center">
          <p className="text-red-600 font-bold">이 출장은 취소되었습니다</p>
        </div>
      )}

      {/* Route Info */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mb-6">
        <h3 className="text-sm font-bold text-gray-800 mb-4">경로 정보</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">출</span>
            <p className="text-sm text-gray-700">{route.start_location}</p>
          </div>
          <div className="ml-3 border-l-2 border-dashed border-gray-200 h-4"/>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">도</span>
            <p className="text-sm text-gray-700">{route.dest_location}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 space-y-1">
        <p>일광오피스 스마트 물류 시스템</p>
        <p>국토교통부 ITS · AWS · Gemini AI 기반</p>
        <p className="text-[10px]">문의: ikoffices.com</p>
      </div>
    </div>
  );
}
